import { logger } from 'common/services';

import type { OpenNatterMessage, SequencedUserNotification } from '../types/messages';

/**
 * Tracks sequence IDs for Open Natter events to drop stale/duplicate messages
 *
 * IMPORTANT: Gaps in sequence numbers are NORMAL and expected.
 * We only track the highest sequence number seen to drop older messages.
 * We do NOT buffer messages waiting for gaps to be filled.
 */
class SequenceChecker {
  // Track highest sequence ID seen per openNatterId
  private readonly highestSequenceIds = new Map<string, number>();

  /**
   * Process a message, checking sequence and dropping stale messages
   * @returns Array with the message if it should be dispatched, empty array if dropped
   */
  processMessage(message: OpenNatterMessage): OpenNatterMessage[] {
    // Non-sequenced messages (broadcast) pass through immediately
    if (message.type !== 'SequencedUserNotification') {
      return [message];
    }

    const sequencedMessage = message as SequencedUserNotification;
    // openNatterId is { value: string }, sequenceId is string (Long from backend)
    const natterId = sequencedMessage.openNatterId.value;
    const sequenceIdNum = parseInt(sequencedMessage.sequenceId, 10);

    const highestSeqId = this.highestSequenceIds.get(natterId);

    // First message for this event - accept it
    if (highestSeqId === undefined) {
      this.highestSequenceIds.set(natterId, sequenceIdNum);
      logger.debug('[SequenceChecker] First message for event', {
        openNatterId: natterId,
        sequenceId: sequenceIdNum,
        type: sequencedMessage.userNotification.type,
      });
      return [message];
    }

    // Message is newer than highest seen - accept it
    if (sequenceIdNum > highestSeqId) {
      this.highestSequenceIds.set(natterId, sequenceIdNum);
      logger.debug('[SequenceChecker] Accepting newer message', {
        openNatterId: natterId,
        sequenceId: sequenceIdNum,
        previousHighest: highestSeqId,
        gap: sequenceIdNum - highestSeqId - 1,
        type: sequencedMessage.userNotification.type,
      });
      return [message];
    }

    // Message is older or duplicate - drop it
    logger.warn('[SequenceChecker] Dropping stale/duplicate message', {
      openNatterId: natterId,
      sequenceId: sequenceIdNum,
      highestSeqId,
      type: sequencedMessage.userNotification.type,
    });
    return [];
  }

  /**
   * Reset sequence tracking for an event (e.g., when leaving)
   */
  reset(openNatterId: string): void {
    logger.info('[SequenceChecker] Resetting sequence tracking', { openNatterId });
    this.highestSequenceIds.delete(openNatterId);
  }

  /**
   * Clear all sequence tracking (e.g., on logout)
   */
  clearAll(): void {
    logger.info('[SequenceChecker] Clearing all sequence tracking');
    this.highestSequenceIds.clear();
  }

  /**
   * Get statistics for debugging
   */
  getStats() {
    const stats = {
      trackedEvents: this.highestSequenceIds.size,
      eventSequences: Array.from(this.highestSequenceIds.entries()).map(([id, seq]) => ({
        openNatterId: id,
        highestSequenceId: seq,
      })),
    };

    logger.debug('[SequenceChecker] Stats', stats);
    return stats;
  }
}

// Singleton instance
export const sequenceChecker = new SequenceChecker();
